import NeptuneElement from '../../NeptuneElement.js';
import { i18nMixin } from '../../../utils/i18nMixin.js';
import { i18n } from '../../../utils/i18n.js';

class SlottedProfileMenu extends i18nMixin(NeptuneElement) {
  templateMarkup = `
        <slot name="trigger"></slot>
        <div role="dialog" aria-label="Profile menu" hidden>
            <slot name="header"></slot>
            <neptune-menu id="slottedProfileMenuList" role="menu"></neptune-menu>
            <slot name="footer"></slot>
        </div>
    `;
  componentCssUrl = '/src/components/chrome/slottedProfilemenu/slotted-profile-menu.css';

  #outsideClickHandler = null;
  #keydownHandler = null;
  #menuSelectHandler = null;
  #menuNavigateHandler = null;
  #anchorElement = null;
  #previouslyFocused = null;
  #slottedTriggerHandler = null;

  constructor() {
    super();
    this.menuItems = [];
  }

  async connectedCallback() {
    await super.connectedCallback();
    const culture = this.getAttribute('culture');
    if (culture) {
      i18n.setLocale(culture);
    }
    this.setEventListeners();
    this.#setupSlottedTrigger();

    const itemsAttr = this.getAttribute('items');
    if (itemsAttr) {
      try {
        const parsed = JSON.parse(itemsAttr);
        if (Array.isArray(parsed)) { this.menuItems = parsed; }
      } catch { /* ignore */ }
    }

    this.#renderMenuItems();
    if (this.hasAttribute('open')) {
      this.#openMenu();
    }
  }

  disconnectedCallback() {
    this.removeEventListeners();
    super.disconnectedCallback?.();
  }

  static get observedAttributes() {
    return ['open', 'items', 'culture'];
  }


  attributeChangedCallback(name, oldValue, newValue) {
    if (!this.isRendered) {
      return;
    }
    if (name === 'culture' && newValue) {
      i18n.setLocale(newValue);
      this.#applyTranslations();
      return;
    }

    if (name === 'open') {
      this.hasAttribute('open') ? this.#openMenu() : this.#closeMenu();
    } else if (name === 'items') {
      try {
        const parsed = JSON.parse(newValue);
        if (Array.isArray(parsed)) {
          this.menuItems = parsed;
          this.#renderMenuItems();
        }
      } catch { /* invalid JSON, ignore */ }
    }
  }

  onLocaleChange() {
    this.#applyTranslations();
  }

  #applyTranslations() {
    const dialog = this.shadowRoot?.querySelector('div[role="dialog"]');

    if (dialog) {
        dialog.setAttribute('aria-label', this.translate('PROFILE_MENU.ARIA_LABEL'));
    }
    this.#renderMenuItems();
  }

  /**
   * opens the menu, optionally anchored to a trigger element.
   * @param {HTMLElement} [anchorElement]
   * @returns {void}
   */
  open(anchorElement) {
    this.#anchorElement = anchorElement || this.#anchorElement;
    this.setAttribute('open', '');
  }

  close() {
    this.removeAttribute('open');
  }

  /**
   * toggles the menu open/close.
   * @param {HTMLElement} [anchorElement]
   * @returns {void}
   */
  toggle(anchorElement) {
    this.hasAttribute('open') ? this.close() : this.open(anchorElement);
  }

  setEventListeners() {
    this.removeEventListeners();
    const menuList = this.shadowRoot?.querySelector('#slottedProfileMenuList');
    if (!menuList) {
      return;
    }

    this.#menuSelectHandler = (event) => this.#handleMenuItemSelect(event);
    this.#menuNavigateHandler = (event) => this.#handleMenuNavigate(event);
    menuList.addEventListener('menu-item-select', this.#menuSelectHandler);
    menuList.addEventListener('neptune-menu-navigate', this.#menuNavigateHandler);
  }

  removeEventListeners() {
    const menuList = this.shadowRoot?.querySelector('#slottedProfileMenuList');
    if (menuList) {
      if (this.#menuSelectHandler) { 
        menuList.removeEventListener('menu-item-select', this.#menuSelectHandler);
      }
      if (this.#menuNavigateHandler) {
        menuList.removeEventListener('neptune-menu-navigate', this.#menuNavigateHandler);
      }
    }
    this.#menuSelectHandler = null;
    this.#menuNavigateHandler = null;
    this.#removeGlobalListeners();
  }

  /**
    * Dispatches profile-menu-select.
   * @param {object} selected - The selected menu item config.
   * @returns {void}
   */
  #emitSelect(selected) {
    this.dispatchEvent(
      new CustomEvent('profile-menu-select', {
        bubbles: true,
        composed: true,
        detail: { ...selected },
      }),
    );
  }

  /**
  * Renders the configurable menu items inside neptune-menu. Each item is
  * projected into the `menu-item` slot and carries `data-id` for lookup.
  * @returns {void}
  */
  #renderMenuItems() {
    if (!this.isRendered) {
      return;
    }
    const menuList = this.shadowRoot?.querySelector('#slottedProfileMenuList');
    if (!menuList) {
      return;
    }
    menuList.innerHTML = '';

    this.menuItems.forEach((item) => {
      const menuItem = document.createElement('neptune-menu-item');
      menuItem.setAttribute('slot', 'menu-item');
      menuItem.dataset.id = item.id;
      if (item.divider) {
        menuItem.setAttribute('divider', '');
      }
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'profile-menu-item-button';
      button.setAttribute('aria-label', item.name);

      if (item.icon) {
        const icon = document.createElement('i');
        icon.className = item.icon;
        icon.setAttribute('aria-hidden', 'true');
        button.appendChild(icon);
      }
      button.append(item.name);
      menuItem.appendChild(button);
      menuList.appendChild(menuItem);
    });
  }

  /**
   * Handles menu item selection and emits profile-menu-select.
   * @param {CustomEvent} event
   * @returns {void}
   */
  #handleMenuItemSelect(event) {
    const id = event.detail?.item?.dataset?.id;
    const selected = this.menuItems.find(
      (menuItem) => String(menuItem.id) === String(id),
    );
    if (!selected) {
      return;
    }
    this.#emitSelect(selected);
    this.close();
    if (selected.route) {
      window.dispatchEvent(
        new CustomEvent('neptune-location-change', {
          detail: { route: selected.route },
        }),
      );
    }

  }

  /**
   * Handles keyboard navigation events bubbled from menu items.
   * @param {CustomEvent} event
   * @returns {void}
   */
  #handleMenuNavigate(event) {
    const { key, sourceItem } = event.detail || {};
    if (key === 'Escape') {
      this.close();
      return;
    }
    const items = Array.from(
      this.shadowRoot?.querySelectorAll('neptune-menu-item:not([disabled])') ||
        [],
    );
    if (!items.length) {
      return;
    }
    const currentIndex = items.indexOf(sourceItem);
    let nextIndex = currentIndex;

    if (key === 'ArrowDown' || key === 'Down') {
      nextIndex = (currentIndex + 1) % items.length;
    } else if (key === 'ArrowUp' || key === 'Up') {
      nextIndex = (currentIndex - 1 + items.length) % items.length;
    } else if (key === 'Home') {
      nextIndex = 0;
    } else if (key === 'End') {
      nextIndex = items.length - 1;
    }

    items[nextIndex]?.focus();
  }

  /**
  * Opens the menu, positions it relative to the trigger, focuses the first item,
  * attaches global listeners, and dispatches `profile-menu-open`.
  * @returns {void}
  */
  #openMenu() {
    const container = this.shadowRoot?.querySelector('div[role="dialog"]');
    if (!container) {
      return;
    }

    container.hidden = false;

    this.#previouslyFocused = document.activeElement;
    this.#positionMenu();
    this.#attachGlobalListeners();

    requestAnimationFrame(() => {
      this.shadowRoot
        ?.querySelectorAll('neptune-menu-item[selected]')
        .forEach(item => item.removeAttribute('selected'));
    });

    const firstItem = this.shadowRoot?.querySelector(
      'neptune-menu-item:not([disabled])',
    );
    firstItem?.focus?.();

    this.dispatchEvent(
      new CustomEvent('profile-menu-open', { bubbles: true, composed: true }),
    );
  }

  /**
   * Closes the menu, restores focus, and removes global listeners.
   * @returns {void}
   */
  #closeMenu() {
    const container = this.shadowRoot?.querySelector('div[role="dialog"]');
    if (!container) {
      return;
    }
    container.hidden = true;
    this.#removeGlobalListeners();

    if (
      this.#previouslyFocused &&
      typeof this.#previouslyFocused.focus === 'function'
    ) {
      this.#previouslyFocused.focus();
    }
    this.#previouslyFocused = null;

    this.dispatchEvent(
      new CustomEvent('profile-menu-close', {
        bubbles: true,
        composed: true,
      }),
    );
  }

  /**
   * Positions the menu container relative to the anchor (profile icon) if
   * one is provided. Falls back to CSS-driven positioning otherwise.
   * @returns {void}
  */
  #positionMenu() {
    if (!this.#anchorElement) {
      return;
    }
    const container = this.shadowRoot?.querySelector('div[role="dialog"]');
    if (!container) {
      return;
    }

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const triggerRect = this.#anchorElement.getBoundingClientRect();
    const menuWidth = container.offsetWidth || 280;
    const menuMaxHeight = 400;
    const gap = 8;

    let top = triggerRect.bottom - gap;
    const availableHeightBelow = viewportHeight - top;
    if (availableHeightBelow < 100 && triggerRect.top > 200) {
      top = triggerRect.top - Math.min(menuMaxHeight, 200) - gap;
    }

    let left;
    if (triggerRect.right > viewportWidth * 0.75) {
      left = triggerRect.right - menuWidth;
    } else if (triggerRect.left < viewportWidth * 0.25) {
      left = triggerRect.left;
    } else {
      left = triggerRect.left + (triggerRect.width / 2) - (menuWidth / 2);
    }

    if (left < gap) {
      left = gap;
    } else if (left + menuWidth > viewportWidth - gap) {
      left = viewportWidth - menuWidth - gap;
    }

    container.style.setProperty('--menu-top', `${Math.max(gap, top)}px`);
    container.style.setProperty('--menu-left', `${left}px`);
  }

  /**
  * Wires the component to an element placed in the trigger slot.
  * @returns {void}
  */
  #setupSlottedTrigger() {
    const slot = this.shadowRoot?.querySelector('slot[name="trigger"]');
    if (!slot) {
      return;
    }

    const bind = () => {
      const trigger = slot.assignedElements({ flatten: true })[0];
      if (!trigger) {
        return;
      }
      if (this.#slottedTriggerHandler) {
        trigger.removeEventListener('click', this.#slottedTriggerHandler);
      }
      this.#slottedTriggerHandler = (event) => {
        event.stopPropagation();
        this.toggle(trigger);
      };
      trigger.addEventListener('click', this.#slottedTriggerHandler);
    };

    slot.addEventListener('slotchange', bind);
    bind();
  }

  /**
   * Attaches outside-click and Escape key listeners.
   * @returns {void}
   */
  #attachGlobalListeners() {
    this.#outsideClickHandler = (event) => {
      const path = event.composedPath();
      if (
        path.includes(this) ||
        (this.#anchorElement && path.includes(this.#anchorElement))
      ) {
        return;
      }
      this.close();
    };
    this.#keydownHandler = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        this.close();
      }
    };
    document.addEventListener('click', this.#outsideClickHandler, true);
    document.addEventListener('keydown', this.#keydownHandler, true);
  }

  /**
   * Removes global outside-click and Escape key listeners.
   * @returns {void}
   */
  #removeGlobalListeners() {
    if (this.#outsideClickHandler) {
      document.removeEventListener('click', this.#outsideClickHandler, true);
    }
    if (this.#keydownHandler) {
      document.removeEventListener('keydown', this.#keydownHandler, true);
    }
    this.#outsideClickHandler = null;
    this.#keydownHandler = null;
  }

}

customElements.define('neptune-slotted-profile-menu', SlottedProfileMenu);
export default SlottedProfileMenu;